import { 
  signInWithPopup,
  signOut, 
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';

// Admin email addresses (in production, store this in environment variables)
const ADMIN_EMAILS = [
  'rohithdiya@gmail.com',
  'itsyoboyrohith@gmail.com'
];

export const authService = {
  // Sign in with Google
  async signInWithGoogle() {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Check if user is admin
      const isAdmin = await this.isAdmin(user.uid);
      if (!isAdmin) {
        await this.signOut();
        throw new Error('Access denied. Admin privileges required.');
      }
      
      // Create or update user document in Firestore
      await this.createOrUpdateUserDocument(user);
      
      return user;
    } catch (error) {
      console.error('Google sign in error:', error);
      throw error;
    }
  },

  // Sign out
  async signOut() {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  },

  // Check if user is admin
  async isAdmin(uid) {
    try {
      // Check if user email is in admin list
      const user = auth.currentUser;
      if (user && ADMIN_EMAILS.includes(user.email)) {
        return true;
      }
      
      // Check user document in Firestore for admin role
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.role === 'admin';
      }
      
      return false;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  },

  // Create or update user document in Firestore
  async createOrUpdateUserDocument(user) {
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      const userData = {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        lastSignIn: new Date().toISOString(),
        isAdmin: ADMIN_EMAILS.includes(user.email)
      };
      
      if (userDoc.exists()) {
        // Update existing user
        await setDoc(userRef, {
          ...userDoc.data(),
          ...userData
        }, { merge: true });
      } else {
        // Create new user
        await setDoc(userRef, {
          ...userData,
          role: ADMIN_EMAILS.includes(user.email) ? 'admin' : 'user',
          createdAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error creating/updating user document:', error);
    }
  },

  // Create admin user document (for manual setup)
  async createAdminUserDocument(email, displayName) {
    try {
      // This is for manually creating admin user documents
      // The actual user will sign in with Google
      const adminUserRef = doc(db, 'adminUsers', email);
      await setDoc(adminUserRef, {
        email: email,
        displayName: displayName,
        role: 'admin',
        createdAt: new Date().toISOString(),
        isActive: true
      });
      
      return { email, displayName, role: 'admin' };
    } catch (error) {
      console.error('Create admin user document error:', error);
      throw error;
    }
  },

  // Get current user
  getCurrentUser() {
    return auth.currentUser;
  },

  // Listen to auth state changes
  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, callback);
  },

  // Check if user is authenticated and is admin
  async isAuthenticatedAdmin() {
    const user = this.getCurrentUser();
    if (!user) return false;
    
    return await this.isAdmin(user.uid);
  }
};
